import { getDashboardData } from "@/services/admin";

export type DashboardPeriod =
  | "today"
  | "yesterday"
  | "week"
  | "month"
  | "quarter"
  | "year"
  | "custom";

export interface DashboardFilters {
  period?: DashboardPeriod;
  start_date?: string;
  end_date?: string;
  State_Code?: string;
  City_Code?: string;
  Zone_Code?: string;
  Branch_Code?: string;
  School_Code?: string;
  Plan_Name?: string;
}

export type DashboardQuery = DashboardFilters;

export async function fetchDashboard(
  filters: DashboardFilters = {}
): Promise<DashboardResponse> {
  const params: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params[key] = String(value);
    }
  });

  const response = await getDashboardData(params);

  if (!response) {
    throw new Error("Dashboard request failed: no response received");
  }

  const data: DashboardResponse =
    typeof response === "object" && response !== null && "data" in response
      ? (response as { data: DashboardResponse }).data
      : (response as any);

  if (!data || !data.success) {
    throw new Error("Dashboard API returned success: false");
  }

  return data;
}

export interface DashboardOverview {
  Total_Customers: number;
  Active_Customers: number;
  New_Customers: number;
  Existing_Customers: number;
  Total_Children: number;
  Active_Subscriptions: number;
  "Today's_Collection": number;
  Pending_Amount: number;
  Total_Renewals: number;
}

export interface CustomerSummary {
  Total_Customers: number;
  Active_Customers: number;
  New_Customers: number;
  Existing_Customers: number;
  Payment_Customers: number;
}

export interface SubscriptionSummary {
  Active: number;
  Expired: number;
  Starting_Tomorrow: number;
  Starting_Next_7_Days: number;
  Expiring_0_3_Days: number;
  Expiring_4_7_Days: number;
  Expiring_8_15_Days: number;
  Expiring_16_30_Days: number;
  Verification_Pending: number;
  Verification_Completed: number;
  Payment_Pending: number;
  Payment_Paid: number;
}

export interface RenewalSummary {
  Renewals: number;
  New_Subscriptions: number;
  Due_Today: number;
  Due_Next_3_Days: number;
  Due_Next_7_Days: number;
  Expired: number;
}

export interface PaymentMethodBreakdown {
  Payment_Method: string;
  Count: number;
  Amount: number;
}

export interface PaymentSummary {
  Collected: number;
  Pending_Amount: number;
  Failed_Amount: number;
  Transactions: number;
  Success_Transactions: number;
  Pending_Transactions: number;
  Failed_Transactions: number;
  Payment_Methods: PaymentMethodBreakdown[];
}

export interface TrialSummary {
  Total_Trials: number;
  Active_Trials: number;
  Expired_Trials: number;
  Converted: number;
  Not_Converted: number;
  Conversion_Rate: number;
  Trial_Days: number;
}

export interface VerificationSummary {
  Total: number;
  Pending: number;
  Verified: number;
  Verification_Rate: number;
}

export interface UpcomingStarts {
  Tomorrow: number;
  Next_3_Days: number;
  Next_7_Days: number;
  Next_15_Days: number;
  Next_30_Days: number;
}

export interface DeliveryStage {
  Total: number;
  Completed?: number;
  Delivered?: number;
  Ready?: number;
  Pending?: number;
}

export interface DeliverySummary {
  Pickup: { Total: number; Completed: number; Pending: number };
  Delivery: { Total: number; Delivered: number; Pending: number };
  Lunch: { Total: number; Ready: number };
  Live_Users: number;
}

export interface BranchPerformance {
  Zone: string;
  Zone_Code: string;
  Branch: string;
  Branch_Code: string;
  Customers: number;
  Children: number;
  Active: number;
  Revenue: number;
  Delivery_Percentage: number;
}

export interface ActionRequired {
  Trial_Expired_Not_Converted: number;
  Verification_Pending: number;
  Delivery_Issues: number;
  Subscriptions_Expiring_Today: number;
  Payments_Pending: number;
  Services_Starting_Tomorrow: number;
}

export interface DashboardResponse {
  success: boolean;
  filters: {
    period: string;
    start_date: string | null;
    end_date: string | null;
    State_Code: string | null;
    City_Code: string | null;
    Zone_Code: string | null;
    Branch_Code: string | null;
    School_Code: string | null;
    Plan_Name: string | null;
  };
  overview: DashboardOverview;
  customers: CustomerSummary;
  subscriptions: SubscriptionSummary;
  renewals: RenewalSummary;
  payments: PaymentSummary;
  trials: TrialSummary;
  verification: VerificationSummary;
  upcoming_starts: UpcomingStarts;
  delivery: DeliverySummary;
  branch_performance: BranchPerformance[];
  action_required: ActionRequired;
  generated_at: string;
}